import { useEffect, useRef, useState } from "react";
import api from "../services/api";
import { useAuth } from "../context/AuthContext";

const formatExpiry = (value) => {
  if (!value) return "";
  try {
    return new Intl.DateTimeFormat("he-IL", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).format(new Date(value));
  } catch {
    return value;
  }
};

export default function TeamViewerPilotPage() {
  const { user } = useAuth();
  const idRef = useRef(null);
  const passwordRef = useRef(null);
  const usernameRef = useRef(null);

  const [statusInfo, setStatusInfo] = useState(null);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [copied, setCopied] = useState(false);
  const requestId = new URLSearchParams(window.location.search).get("request") || "";
  const requestMode = /^TVREQ-[A-Za-z0-9_-]{20,122}$/.test(requestId);

  useEffect(() => {
    api.get("/api/teamviewer-pilot/status")
      .then((response) => setStatusInfo(response.data))
      .catch(() => setStatusInfo(null));
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setMessage("");
    setResult(null);
    setCopied(false);

    const teamviewerId = idRef.current?.value?.trim() || "";
    const password = passwordRef.current?.value || "";
    const username = usernameRef.current?.value?.trim() || "";

    try {
      const response = await api.post("/api/teamviewer-pilot/credentials", {
        teamviewer_id: teamviewerId,
        password,
        username: username || null,
        request_id: requestMode ? requestId : null,
      });
      setResult(response.data);
      if (passwordRef.current) passwordRef.current.value = "";
      setMessage(
        requestMode
          ? "הפרטים נשמרו לבקשה המאובטחת. אפשר לחזור לצ'אט — אין צורך להעתיק סיסמה או הפניה."
          : "ההפניה נוצרה. העתק רק אותה לצ'אט — לא את הסיסמה."
      );
    } catch (error) {
      const detail = error?.response?.data?.detail;
      setMessage(
        typeof detail === "string"
          ? detail
          : "לא ניתן ליצור הפניה כרגע. בדוק את הנתונים ונסה שוב."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const copyReference = async () => {
    if (!result?.credential_ref) return;
    await navigator.clipboard.writeText(result.credential_ref);
    setCopied(true);
  };

  const revokeReference = async () => {
    if (!result?.credential_ref) return;
    try {
      await api.delete(
        `/api/teamviewer-pilot/credentials/${encodeURIComponent(result.credential_ref)}`
      );
    } finally {
      setResult(null);
      setMessage("ההפניה בוטלה.");
    }
  };

  const allowed = user?.role === "owner" || user?.role === "admin";

  if (!allowed) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="rounded-2xl border border-red-200 bg-red-50 p-5 text-red-800">
          אין הרשאה להשתמש ב-TeamViewer Pilot.
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6" dir="rtl">
      <div className="mb-6">
        <p className="text-sm font-semibold text-brand-600">PRIVATE PILOT</p>
        <h1 className="mt-1 text-3xl font-bold text-gray-900">TeamViewer Pilot</h1>
        <p className="mt-2 text-sm leading-6 text-gray-600">
          הזן פרטי TeamViewer זמניים. השרת מחזיק רק עותק מוצפן בזיכרון התהליך,
          ללא Redis, מסד נתונים, קובץ או גיבוי.
          {requestMode
            ? " הבקשה כבר משויכת לצ'אט — אין צורך להעביר אליו סיסמה או הפניה."
            : " לצ'אט מעבירים רק את ההפניה."}
        </p>
      </div>

      <div className="mb-5 grid gap-3 sm:grid-cols-3">
        <div className="rounded-xl border bg-white p-4">
          <div className="text-xs text-gray-500">תוקף</div>
          <div className="mt-1 font-semibold">
            {statusInfo ? `${Math.round(statusInfo.ttl_seconds / 60)} דקות` : "30 דקות"}
          </div>
        </div>
        <div className="rounded-xl border bg-white p-4">
          <div className="text-xs text-gray-500">אחסון</div>
          <div className="mt-1 font-semibold">RAM מוצפן בלבד</div>
        </div>
        <div className="rounded-xl border bg-white p-4">
          <div className="text-xs text-gray-500">Launcher</div>
          <div className="mt-1 font-semibold">
            {statusInfo?.launcher_ready ? "מוכן" : "טרם שויך"}
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="rounded-2xl border bg-white p-5 shadow-sm">
        <label className="block text-sm font-medium text-gray-700">
          TeamViewer ID
          <input
            ref={idRef}
            required
            inputMode="numeric"
            autoComplete="off"
            placeholder="123 456 789"
            className="mt-2 w-full rounded-xl border border-gray-300 px-3 py-2.5 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
          />
        </label>

        <label className="mt-4 block text-sm font-medium text-gray-700">
          Password
          <input
            ref={passwordRef}
            required
            type="password"
            autoComplete="new-password"
            className="mt-2 w-full rounded-xl border border-gray-300 px-3 py-2.5 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
          />
        </label>

        <label className="mt-4 block text-sm font-medium text-gray-700">
          Username <span className="font-normal text-gray-400">(אופציונלי)</span>
          <input
            ref={usernameRef}
            autoComplete="off"
            className="mt-2 w-full rounded-xl border border-gray-300 px-3 py-2.5 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
          />
        </label>

        <button
          type="submit"
          disabled={submitting}
          className="mt-5 w-full rounded-xl bg-brand-600 px-4 py-3 font-semibold text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? "יוצר הפניה..." : "Create secure reference"}
        </button>
      </form>

      {message && (
        <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50 p-4 text-sm text-blue-900">
          {message}
        </div>
      )}

      {result && (
        <div className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
          <div className="text-sm font-semibold text-emerald-900">
            {requestMode ? "Secure request ready" : "Secure reference created"}
          </div>
          {!requestMode && (
            <code
              dir="ltr"
              className="mt-3 block break-all rounded-lg bg-white px-3 py-3 text-sm font-semibold text-gray-900"
            >
              {result.credential_ref}
            </code>
          )}
          <div className="mt-2 text-xs text-emerald-800">
            פג תוקף ב-{formatExpiry(result.expires_at)} · ניתן למימוש פעם אחת בלבד.
          </div>

          <div className="mt-4 flex flex-col gap-2 sm:flex-row">
            {!requestMode && (
              <button
                type="button"
                onClick={copyReference}
                className="flex-1 rounded-xl bg-emerald-700 px-4 py-2.5 font-semibold text-white hover:bg-emerald-800"
              >
                {copied ? "הועתק" : "Copy reference"}
              </button>
            )}
            <button
              type="button"
              onClick={revokeReference}
              className="rounded-xl border border-emerald-300 bg-white px-4 py-2.5 font-semibold text-emerald-900 hover:bg-emerald-100"
            >
              בטל הפניה
            </button>
          </div>
        </div>
      )}

      <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-900">
        אל תדביק סיסמת TeamViewer בצ'אט.
        {requestMode ? " הבקשה הזו כבר משויכת אוטומטית לצ'אט." : " הדבק רק את ה-credential_ref שנוצר כאן."}
        הפעלה מחדש של ה-backend מוחקת מיד את כל ההפניות הפעילות.
      </div>
    </div>
  );
}
